import { computed, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { MatDialog } from '@angular/material/dialog';
import {
  SlotReservationDto,
  SignalRService,
  StudyReviewPresenceSnapshot,
  StudyReviewAccessResult,
} from '@core/services/signal-r/signal-r.service';
import { selectCurrentUserId } from '@core/auth/auth.selectors';
import { selectCurrentStage } from '@core/state/entities/stage/stage.selectors';
import { selectCurrentStageId } from '@core/state/ui/stage/stage-ui.selectors';
import { selectLoadedProjectId } from '@core/state/ui/project/project-ui.selectors';
import { SurplusNotificationDialogComponent } from '@shared/dialogs/surplus-notification-dialog/surplus-notification-dialog.component';
import {
  AccessDeniedDialogComponent,
  AccessDeniedDialogResult,
} from '@shared/dialogs/access-denied-dialog/access-denied-dialog.component';

// ── Constants ──

// Fallback used only while the client is offline and hasn't yet received a
// server snapshot carrying the authoritative `releaseAt`. Once a snapshot
// arrives (on reconnect), the server-stamped absolute timestamp takes over
// and this constant stops being consulted. Any drift during the offline
// window is cosmetic — the user can't save while disconnected, so it doesn't
// affect behaviour, only the countdown display.
const FALLBACK_GRACE_PERIOD_MS = 2 * 60 * 60 * 1000; // 2 hours
const BANNER_VISIBLE_AFTER_MS = 10 * 60 * 1000; // show after 10 min disconnected
// Conservative display buffer: the client reports "expired" this many ms
// before the server's actual release time, so the reviewer is never told
// "you have time left" immediately before a save would be rejected.
const EXPIRY_DISPLAY_BUFFER_MS = 10 * 1000;
// How long a sustained RPC failure burst (server unresponsive at the app
// layer despite a connected transport) must persist before we treat it as
// equivalent to a transport disconnect. Long enough to absorb normal retry
// backoff (2 retries × 1.5s ≈ 2s); short enough that genuinely sustained
// server problems flip into offline UX promptly.
const RPC_FAILURE_THRESHOLD_MS = 3_000;

export type ConnectionState = 'connected' | 'suspended' | 'expired';

export type PresenceDisplayState =
  | 'pending'
  | 'bypass'
  | 'normal'
  | 'idle'
  | 'releasedRoomAvailable'
  | 'releasedDirtyRoomAvailable'
  | 'surplus'
  | 'surplusWithDirtyForm'
  | 'offline'
  | 'offlineExpiredClean'
  | 'offlineExpiredDirty';

const SURPLUS_DISPLAY_STATES: ReadonlySet<PresenceDisplayState> = new Set([
  'surplus',
  'surplusWithDirtyForm',
]);

// ── State shape ──

export interface StudyPresenceEntry {
  sessions: SlotReservationDto[];
  /**
   * Per-stage authoritative allocation from the last server snapshot. Includes
   * phase-specific saved work plus active SlotReservations — see
   * `StudyReviewPresenceSnapshot.totalAllocatedSessionCountByStage`.
   */
  totalAllocatedSessionCountByStage: Record<string, number>;
  /** Persisted aggregate version on the last applied snapshot. */
  aggregateVersion: number | null;
  /** Epoch ms of the persisted server timestamp, used for rolling-version fallback. */
  serverTimestampMs: number;
}

export interface StudyPresenceState {
  studies: { [studyId: string]: StudyPresenceEntry };
  activeProjectId: string | null;
  activeStageId: string | null;
  activeStudyId: string | null;
  /** True when the current reviewer's session was removed from the active study's snapshot. */
  sessionRemoved: boolean;
  /** Epoch ms when the SignalR connection was lost while reviewing. Null when connected. */
  disconnectedSince: number | null;
  /**
   * Flipped exactly once per disconnect window by a setTimeout. Threshold
   * flags can't be derived (Date.now() isn't reactive), so they live in
   * state and a single effect schedules the timer that flips them.
   */
  hasCrossedBannerVisibleThreshold: boolean;
  hasCrossedExpiryThreshold: boolean;
  /** Flipped reactively when the current idle reservation passes its server-derived trust deadline. */
  hasCrossedIdleTrustThreshold: boolean;
  /** True when the annotation form has unsaved typed input. Wired from the component's (isDirty) event. */
  formDirty: boolean;
  /** True when the reviewer has a saved AnnotationSession (graduated past slot enforcement). */
  hasSavedSession: boolean;
  /** Authoritative capacity policy selected by the server for the current claim. */
  capacityEnforced: boolean;
  /** True while a re-acquire JoinStudyReview invocation is in flight. */
  rejoinInFlight: boolean;
  /**
   * Epoch ms of the first unacknowledged RPC failure in the current burst.
   * Set on the first hub-method exception, cleared after every failed method
   * in the current burst has either recovered or become irrelevant.
   * Once sustained beyond `RPC_FAILURE_THRESHOLD_MS`, this timestamp is
   * adopted by `effectiveDisconnectedSince` — anchoring the suspension
   * countdown at the moment the server effectively went silent rather than
   * at the moment we decided to admit it.
   */
  firstUnacknowledgedFailureAt: number | null;
  /**
   * Flipped by the threshold timer once the current RPC failure burst has
   * persisted long enough to be treated as an effective disconnect. Lives
   * in state (not a `Date.now()` read in a computed) so dependent computeds
   * re-evaluate reactively when the threshold is crossed.
   */
  rpcFailurePromoted: boolean;
}

const initialState: StudyPresenceState = {
  studies: {},
  activeProjectId: null,
  activeStageId: null,
  activeStudyId: null,
  sessionRemoved: false,
  disconnectedSince: null,
  hasCrossedBannerVisibleThreshold: false,
  hasCrossedExpiryThreshold: false,
  hasCrossedIdleTrustThreshold: false,
  formDirty: false,
  hasSavedSession: false,
  capacityEnforced: false,
  rejoinInFlight: false,
  firstUnacknowledgedFailureAt: null,
  rpcFailurePromoted: false,
};

// ── Store ──

// Angular signal stores are injectable classes and therefore use PascalCase.
// eslint-disable-next-line @typescript-eslint/naming-convention
export const StudyPresenceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('studyPresence'),

  withComputed((state, globalStore = inject(Store)) => {
    // Bridge global store selectors into signal store computed signals
    const currentUserId = globalStore.selectSignal(selectCurrentUserId);
    const currentStage = globalStore.selectSignal(selectCurrentStage);
    const currentStageId = globalStore.selectSignal(selectCurrentStageId);

    const activeStudyPresence = computed((): StudyPresenceEntry | null => {
      const id = state.activeStudyId();
      return id ? (state.studies()[id] ?? null) : null;
    });

    const activeSessionCount = computed(
      () => activeStudyPresence()?.sessions.length ?? 0
    );

    const mySession = computed((): SlotReservationDto | null => {
      const entry = activeStudyPresence();
      const userId = currentUserId();
      const stageId = state.activeStageId();
      if (!entry || !userId || !stageId) return null;
      return (
        entry.sessions.find(
          (s) =>
            s.investigatorId.toLowerCase() === userId && s.stageId === stageId
        ) ?? null
      );
    });

    const isReviewerSurplus = computed((): boolean => {
      const entry = activeStudyPresence();
      const userId = currentUserId();
      const stage = currentStage();
      const activeStage = state.activeStageId();

      if (!entry || !userId || !stage) return false;
      if (activeStage && stage.id !== activeStage) return false;

      // Total allocated count (saved sessions + live reservations) must cross the
      // target. Using reservations.length alone misses saved reviewers who no
      // longer hold a reservation — the surplus would never be detected in that case.
      const totalAllocated =
        entry.totalAllocatedSessionCountByStage[stage.id] ?? 0;
      if (totalAllocated < stage.sessionCountTarget) return false;

      return !entry.sessions.some(
        (s) =>
          s.investigatorId.toLowerCase() === userId && s.stageId === stage.id
      );
    });

    const shouldShowWarning = isReviewerSurplus;

    const isCurrentReviewerIdle = computed(() => mySession()?.isIdle === true);

    const currentReviewerIdleSince = computed((): Date | null => {
      const session = mySession();
      if (!session?.idleSince) return null;
      return new Date(session.idleSince);
    });

    const idleSessionTimeoutMinutes = computed((): number | null => {
      const stage = currentStage();
      return stage?.idleSessionTimeoutMinutes ?? null;
    });

    /**
     * Effective disconnect timestamp — the earliest of:
     *   (a) transport-reported disconnect (`disconnectedSince`), or
     *   (b) the first RPC failure in a burst that has now persisted longer
     *       than `RPC_FAILURE_THRESHOLD_MS` without recovering.
     *
     * Anchoring at the first failure (not at threshold-trip time) means the
     * suspension countdown reflects when the server effectively went silent,
     * not when the client decided to admit it. (b) lets a server that's
     * unhealthy at the application layer flow into the same offline UX as
     * a transport drop, without losing the original moment for the timer.
     */
    const effectiveDisconnectedSince = computed((): number | null => {
      const transport = state.disconnectedSince();
      const rpc = state.firstUnacknowledgedFailureAt();
      if (transport !== null) {
        // Either signal is canonical; pick the earlier so the suspension
        // countdown anchors at the first sign of trouble.
        return rpc !== null && rpc < transport ? rpc : transport;
      }
      // RPC channel only adopts once the threshold timer has flipped the
      // promotion flag. Brief bursts that recover before promotion stay
      // invisible to the state machine.
      return rpc !== null && state.rpcFailurePromoted() ? rpc : null;
    });

    /**
     * True while a recent RPC failure burst has not yet been resolved AND has
     * not yet promoted into `effectiveDisconnectedSince`. Drives the discreet
     * "Reconnecting..." sync indicator. Once the burst either recovers (clears)
     * or promotes (offline banner takes over), this flips false.
     */
    const isRetrying = computed((): boolean => {
      // We're "retrying" when there's an unacknowledged burst that hasn't
      // yet promoted into effective disconnect. Once promoted, the offline
      // banner takes over the user-visible signal and the indicator hides.
      return (
        state.firstUnacknowledgedFailureAt() !== null &&
        !state.rpcFailurePromoted()
      );
    });

    /**
     * Epoch ms when the reservation started its suspended-grace period.
     * Prefers the server-stamped `mySession()?.suspendedSince` when available;
     * falls back to the client-observed effective disconnect during the
     * initial offline window before any snapshot has arrived.
     */
    const suspendedSinceMs = computed((): number | null => {
      const serverSince = mySession()?.suspendedSince;
      if (serverSince) return Date.parse(serverSince);
      return effectiveDisconnectedSince();
    });

    /**
     * Absolute epoch ms when the server will release this reservation.
     * Prefers the server-authoritative `releaseAt` from the latest snapshot;
     * falls back to `effectiveDisconnectedSince + grace` during the offline
     * window when no post-disconnect snapshot has been received yet.
     */
    const suspendedExpiresAt = computed((): number | null => {
      const serverReleaseAt = mySession()?.releaseAt;
      if (serverReleaseAt) return Date.parse(serverReleaseAt);
      const since = effectiveDisconnectedSince();
      return since !== null ? since + FALLBACK_GRACE_PERIOD_MS : null;
    });

    /** Epoch ms at which the disconnection banner should become visible. */
    const bannerVisibleAt = computed((): number | null => {
      const since = effectiveDisconnectedSince();
      return since === null ? null : since + BANNER_VISIBLE_AFTER_MS;
    });

    /**
     * Epoch ms at which the client should flip from `suspended` to `expired`
     * (server release minus a conservative display buffer).
     */
    const expiryAt = computed((): number | null => {
      const at = suspendedExpiresAt();
      return at === null ? null : at - EXPIRY_DISPLAY_BUFFER_MS;
    });

    const connectionState = computed((): ConnectionState => {
      if (effectiveDisconnectedSince() === null) return 'connected';
      return state.hasCrossedExpiryThreshold() ? 'expired' : 'suspended';
    });

    const shouldShowCountdown = computed(
      (): boolean =>
        effectiveDisconnectedSince() !== null &&
        state.hasCrossedBannerVisibleThreshold() &&
        !state.hasCrossedExpiryThreshold()
    );

    /**
     * Per-state local trust window for `idle` — the only slot-fact assertion
     * with a known finite validity. The server marks the reviewer idle, then
     * counts down to remove the slot at `idleSince + idleSessionTimeoutMinutes`.
     * Past that deadline, the server has likely removed the slot but we may
     * not have received the resulting snapshot yet — so we collapse to
     * `pending` rather than continuing to assert idle.
     *
     * Other slot-fact assertions (`normal`, `released*`, `surplus*`) are
     * trusted while connected; they don't have a time-based degradation
     * because they can only flip in response to a server event that produces
     * a snapshot.
     */
    const idleTrustedUntilMs = computed((): number | null => {
      const session = mySession();
      const timeoutMin = idleSessionTimeoutMinutes();
      if (
        session?.idleSince === null ||
        session?.idleSince === undefined ||
        timeoutMin === null
      ) {
        return null;
      }
      return Date.parse(session.idleSince) + timeoutMin * 60_000;
    });

    const isIdleStateTrustworthy = computed((): boolean => {
      const until = idleTrustedUntilMs();
      return until === null || !state.hasCrossedIdleTrustThreshold();
    });

    const isReservationExpired = computed(
      () => connectionState() === 'expired'
    );

    const isSessionExpired = computed(
      () => state.sessionRemoved() && !isReviewerSurplus()
    );

    const presenceDisplayState = computed((): PresenceDisplayState => {
      if (state.hasSavedSession()) return 'bypass';

      const conn = connectionState();
      if (conn === 'expired') {
        return state.formDirty()
          ? 'offlineExpiredDirty'
          : 'offlineExpiredClean';
      }
      if (shouldShowCountdown()) return 'offline';

      // We're on a study but the first server snapshot hasn't arrived yet.
      // Slot status is genuinely unknown — treat the gap as pending so the
      // parent can hold the form region (and any banner) until reconciliation
      // lands. Without this guard, `mySession()` returns null transiently and
      // we'd flash a `releasedRoomAvailable` banner on every navigation.
      if (state.activeStudyId() !== null && activeStudyPresence() === null) {
        return 'pending';
      }

      // connected (or transient reconnecting — last value sticks until snapshot)
      if (state.sessionRemoved() || mySession() === null) {
        if (isReviewerSurplus()) {
          return state.formDirty() ? 'surplusWithDirtyForm' : 'surplus';
        }
        return state.formDirty()
          ? 'releasedDirtyRoomAvailable'
          : 'releasedRoomAvailable';
      }
      if (mySession()?.isIdle === true) {
        // Server marked us idle. The state remains trustworthy until
        // idleSince + idleSessionTimeoutMinutes; past that the server has
        // likely removed our slot but we may not have received the snapshot
        // yet — collapse to `pending` so no banner asserts an outdated fact.
        return isIdleStateTrustworthy() ? 'idle' : 'pending';
      }
      if (isReviewerSurplus()) {
        return state.formDirty() ? 'surplusWithDirtyForm' : 'surplus';
      }
      return 'normal';
    });

    /**
     * Lock the form (replace it with an overlay) only in the two states where
     * the form has nothing to preserve and the slot is definitively gone or
     * unreachable: `surplus` (clean, study at capacity) and `offlineExpiredClean`
     * (clean, offline beyond grace).
     */
    const shouldLockForm = computed((): boolean => {
      const s = presenceDisplayState();
      return s === 'surplus' || s === 'offlineExpiredClean';
    });

    const activeSessionsForCurrentStage = computed((): SlotReservationDto[] => {
      const stageId = currentStageId();
      if (!stageId) return [];

      const seen = new Set<string>();
      const result: SlotReservationDto[] = [];
      for (const entry of Object.values(state.studies())) {
        for (const session of entry.sessions) {
          if (
            session.stageId.toLowerCase() === stageId.toLowerCase() &&
            !seen.has(session.investigatorId.toLowerCase())
          ) {
            seen.add(session.investigatorId.toLowerCase());
            result.push(session);
          }
        }
      }
      return result;
    });

    return {
      activeStudyPresence,
      activeSessionCount,
      mySession,
      isReviewerSurplus,
      isSessionExpired,
      shouldShowWarning,
      shouldLockForm,
      isCurrentReviewerIdle,
      currentReviewerIdleSince,
      idleSessionTimeoutMinutes,
      idleTrustedUntilMs,
      activeSessionsForCurrentStage,
      effectiveDisconnectedSince,
      isRetrying,
      suspendedSinceMs,
      suspendedExpiresAt,
      bannerVisibleAt,
      expiryAt,
      connectionState,
      shouldShowCountdown,
      isReservationExpired,
      presenceDisplayState,
    };
  }),

  withMethods(
    (
      store,
      signalR = inject(SignalRService),
      dialog = inject(MatDialog),
      globalStore = inject(Store),
      router = inject(Router)
    ) => {
      /** Track which studyIds have had the access-denied dialog shown (per-session). */
      const deniedStudyIds = new Set<string>();
      const currentUserId = globalStore.selectSignal(selectCurrentUserId);
      const currentProjectId = globalStore.selectSignal(selectLoadedProjectId);

      // Timer handles for the threshold flips. Live in the closure (not state —
      // they're OS resources, not data). Scheduled and cancelled by a single
      // helper called from each lifecycle method that mutates `disconnectedSince`,
      // `firstUnacknowledgedFailureAt`, or `releaseAt`.
      let bannerVisibleTimer: ReturnType<typeof setTimeout> | null = null;
      let expiryTimer: ReturnType<typeof setTimeout> | null = null;
      let idleTrustTimer: ReturnType<typeof setTimeout> | null = null;
      // Separate timer for promoting an RPC failure burst into effective
      // disconnect once it crosses the threshold. Scheduled when
      // `firstUnacknowledgedFailureAt` is set; cancelled on recovery.
      let rpcFailureThresholdTimer: ReturnType<typeof setTimeout> | null = null;

      const cancelBannerVisibleTimer = (): void => {
        if (bannerVisibleTimer !== null) {
          clearTimeout(bannerVisibleTimer);
          bannerVisibleTimer = null;
        }
      };
      const cancelExpiryTimer = (): void => {
        if (expiryTimer !== null) {
          clearTimeout(expiryTimer);
          expiryTimer = null;
        }
      };
      const cancelIdleTrustTimer = (): void => {
        if (idleTrustTimer !== null) {
          clearTimeout(idleTrustTimer);
          idleTrustTimer = null;
        }
      };
      const cancelRpcFailureThresholdTimer = (): void => {
        if (rpcFailureThresholdTimer !== null) {
          clearTimeout(rpcFailureThresholdTimer);
          rpcFailureThresholdTimer = null;
        }
      };

      /**
       * Schedule the RPC-failure-threshold timer. Anchors at the burst's first
       * failure timestamp; when it fires, the patchState is a no-op (the field
       * is already set), but `effectiveDisconnectedSince` re-derives and
       * `rescheduleTimers` runs to bring the banner-visible / expiry timers in
       * line with the now-promoted disconnect.
       */
      const scheduleRpcFailureThresholdPromotion = (): void => {
        cancelRpcFailureThresholdTimer();
        const firstFailure = store.firstUnacknowledgedFailureAt();
        if (firstFailure === null) return;
        const promoteAt = firstFailure + RPC_FAILURE_THRESHOLD_MS;
        const delay = promoteAt - Date.now();
        const fire = (): void => {
          rpcFailureThresholdTimer = null;
          // Promotion is the moment effectiveDisconnectedSince transitions from
          // null to non-null via the RPC channel. Flip the promotion flag (the
          // canonical reactive trigger), and reset the banner-visible / expiry
          // threshold flags so this opens a fresh disconnect window rather than
          // honouring stale flags from a previous one.
          if (store.firstUnacknowledgedFailureAt() === null) return;
          patchState(store, {
            rpcFailurePromoted: true,
            hasCrossedBannerVisibleThreshold: false,
            hasCrossedExpiryThreshold: false,
          });
          rescheduleTimers();
        };
        if (delay <= 0) {
          fire();
        } else {
          rpcFailureThresholdTimer = setTimeout(fire, delay);
        }
      };

      /**
       * Cancel any pending timers and (re)schedule them based on the current
       * derived `bannerVisibleAt`/`expiryAt` and the threshold flags. Idempotent:
       * calling it twice in a row is a no-op for state.
       */
      const rescheduleTimers = (): void => {
        cancelBannerVisibleTimer();
        cancelExpiryTimer();
        cancelIdleTrustTimer();

        const bannerAt = store.bannerVisibleAt();
        if (bannerAt !== null && !store.hasCrossedBannerVisibleThreshold()) {
          const delay = bannerAt - Date.now();
          if (delay <= 0) {
            patchState(store, { hasCrossedBannerVisibleThreshold: true });
          } else {
            bannerVisibleTimer = setTimeout(() => {
              patchState(store, { hasCrossedBannerVisibleThreshold: true });
              bannerVisibleTimer = null;
            }, delay);
          }
        }

        const expAt = store.expiryAt();
        if (expAt !== null && !store.hasCrossedExpiryThreshold()) {
          const delay = expAt - Date.now();
          if (delay <= 0) {
            patchState(store, { hasCrossedExpiryThreshold: true });
          } else {
            expiryTimer = setTimeout(() => {
              patchState(store, { hasCrossedExpiryThreshold: true });
              expiryTimer = null;
            }, delay);
          }
        }

        const idleTrustAt = store.idleTrustedUntilMs();
        if (idleTrustAt !== null && !store.hasCrossedIdleTrustThreshold()) {
          const delay = idleTrustAt - Date.now();
          if (delay <= 0) {
            patchState(store, { hasCrossedIdleTrustThreshold: true });
          } else {
            idleTrustTimer = setTimeout(() => {
              patchState(store, { hasCrossedIdleTrustThreshold: true });
              idleTrustTimer = null;
            }, delay);
          }
        }
      };

      /**
       * Tear down a study presence: drop its snapshot, clear active fields if it
       * is the active study, and notify the server. Used directly by `leaveStudy`
       * and indirectly by `joinStudy` when transitioning between studies, so a
       * fresh join always supersedes the previous one without relying on a
       * separate component-side cleanup path.
       */
      const releaseStudy = (
        projectId: string,
        stageId: string,
        studyId: string
      ): void => {
        patchState(store, (state) => {
          const remaining = { ...state.studies };
          delete remaining[studyId];
          const isActive = state.activeStudyId === studyId;
          return {
            studies: remaining,
            activeProjectId: isActive ? null : state.activeProjectId,
            activeStageId: isActive ? null : state.activeStageId,
            activeStudyId: isActive ? null : state.activeStudyId,
            sessionRemoved: isActive ? false : state.sessionRemoved,
            formDirty: isActive ? false : state.formDirty,
            hasSavedSession: isActive ? false : state.hasSavedSession,
            capacityEnforced: isActive ? false : state.capacityEnforced,
          };
        });
        rescheduleTimers();
        signalR.leaveStudyReview(projectId, stageId, studyId);
      };

      return {
        /** Called when SignalR delivers a presence snapshot. */
        presenceUpdated(snapshot: StudyReviewPresenceSnapshot): void {
          // Last-write-wins by persisted aggregate version. Timestamp fallback keeps
          // rolling deployments compatible with older API replicas.
          const incomingTs = Date.parse(snapshot.serverTimestamp);
          const incomingVersion = snapshot.aggregateVersion ?? null;
          const existing = store.studies()[snapshot.studyId];
          const isStale =
            existing &&
            (incomingVersion !== null && existing.aggregateVersion !== null
              ? incomingVersion <= existing.aggregateVersion
              : incomingTs <= existing.serverTimestampMs);
          if (isStale) {
            return;
          }

          // Detect if the current reviewer's session was removed from the active study
          const isActiveStudy = snapshot.studyId === store.activeStudyId();
          let hadSession = false;
          if (isActiveStudy) {
            patchState(store, { hasCrossedIdleTrustThreshold: false });
            const userId = currentUserId();
            const stageId = store.activeStageId();
            hadSession =
              !!userId &&
              !!existing?.sessions.some(
                (s) =>
                  s.investigatorId.toLowerCase() === userId &&
                  s.stageId === stageId
              );
          }

          patchState(store, (state) => ({
            studies: {
              ...state.studies,
              [snapshot.studyId]: {
                sessions: snapshot.reservations,
                totalAllocatedSessionCountByStage:
                  snapshot.totalAllocatedSessionCountByStage,
                aggregateVersion: incomingVersion,
                serverTimestampMs: incomingTs,
              },
            },
          }));

          if (isActiveStudy) {
            const userId = currentUserId();
            const stageId = store.activeStageId();
            const stillHasSession =
              !!userId &&
              snapshot.reservations.some(
                (s) =>
                  s.investigatorId.toLowerCase() === userId &&
                  s.stageId === stageId
              );
            // Symmetric reconciliation (R3):
            //   hadSession && !stillHasSession → set sessionRemoved
            //   stillHasSession                → clear sessionRemoved + clear rejoinInFlight
            //   neither                        → leave alone
            if (hadSession && !stillHasSession) {
              patchState(store, { sessionRemoved: true });
            } else if (stillHasSession) {
              patchState(store, {
                sessionRemoved: false,
                rejoinInFlight: false,
              });
            }
          }

          // Snapshots may carry an updated `releaseAt`, which feeds `expiryAt`.
          // Always reschedule so the expiry timer tracks the latest value.
          rescheduleTimers();
        },

        /**
         * Called when the reviewer navigates to a study review page.
         *
         * If a different study is currently active, releases it first (server
         * notification + state cleanup) so a transition is atomic from the
         * caller's perspective. Re-joining the same study (e.g. form-dirty
         * auto-rejoin after an idle timeout) skips the release step.
         */
        joinStudy(projectId: string, stageId: string, studyId: string): void {
          const previousStudyId = store.activeStudyId();
          if (previousStudyId !== null && previousStudyId !== studyId) {
            const previousProjectId = store.activeProjectId();
            const previousStageId = store.activeStageId();
            if (previousProjectId !== null && previousStageId !== null) {
              releaseStudy(previousProjectId, previousStageId, previousStudyId);
            }
          }

          patchState(store, {
            activeProjectId: projectId,
            activeStageId: stageId,
            activeStudyId: studyId,
            sessionRemoved: false,
            rejoinInFlight: false,
            hasCrossedIdleTrustThreshold: false,
            capacityEnforced:
              previousStudyId === studyId ? store.capacityEnforced() : false,
          });
          signalR.joinStudyReview(projectId, stageId, studyId);
        },

        /** Re-run the idempotent join to refresh a capacity policy changed by project updates. */
        refreshAccessPolicy(): void {
          const projectId = store.activeProjectId();
          const stageId = store.activeStageId();
          const studyId = store.activeStudyId();
          if (!projectId || !stageId || !studyId) return;
          signalR.joinStudyReview(projectId, stageId, studyId);
        },

        /** Called when the reviewer navigates away from a study. */
        leaveStudy(projectId: string, stageId: string, studyId: string): void {
          releaseStudy(projectId, stageId, studyId);
        },

        /** Reset all presence state (e.g. on logout). */
        clearPresence(): void {
          cancelBannerVisibleTimer();
          cancelExpiryTimer();
          cancelIdleTrustTimer();
          cancelRpcFailureThresholdTimer();
          patchState(store, initialState);
        },

        /** Called when the annotation form becomes dirty (any field filled in). */
        startedAnnotating(
          projectId: string,
          stageId: string,
          studyId: string
        ): void {
          signalR.startedAnnotating(projectId, stageId, studyId);
        },

        /** Called when annotations are removed and the form returns to clean without a saved session. */
        stoppedAnnotating(
          projectId: string,
          stageId: string,
          studyId: string
        ): void {
          signalR.stoppedAnnotating(projectId, stageId, studyId);
        },

        /** Called when the SignalR connection drops while the reviewer is on a study. */
        connectionLost(): void {
          if (store.activeStudyId()) {
            patchState(store, {
              disconnectedSince: Date.now(),
              hasCrossedBannerVisibleThreshold: false,
              hasCrossedExpiryThreshold: false,
            });
            rescheduleTimers();
          }
        },

        /** Called when the SignalR connection is restored. */
        connectionRestored(): void {
          // Optimistic clear of sessionRemoved — the rejoin will reconcile via
          // snapshot or rejoinFailed. The user briefly sees a normal state for
          // 200–500 ms; then the rejoin's snapshot or denial pins it definitively.
          cancelBannerVisibleTimer();
          cancelExpiryTimer();
          patchState(store, {
            disconnectedSince: null,
            hasCrossedBannerVisibleThreshold: false,
            hasCrossedExpiryThreshold: false,
            sessionRemoved: false,
          });
        },

        /** Called when the post-reconnection JoinStudyReview is denied. */
        rejoinFailed(): void {
          cancelBannerVisibleTimer();
          cancelExpiryTimer();
          patchState(store, {
            disconnectedSince: null,
            hasCrossedBannerVisibleThreshold: false,
            hasCrossedExpiryThreshold: false,
            sessionRemoved: true,
            rejoinInFlight: false,
          });
        },

        /**
         * Recorded the moment a hub-method invocation throws. Idempotent:
         * subsequent failures in the same burst do not advance the timestamp,
         * preserving the original "first signal of trouble" anchor.
         */
        rpcFailureStarted(timestamp: number): void {
          if (store.firstUnacknowledgedFailureAt() !== null) return;
          patchState(store, { firstUnacknowledgedFailureAt: timestamp });
          scheduleRpcFailureThresholdPromotion();
        },

        /**
         * Any successful hub-method invocation. Clears the burst-failure
         * timestamp + promotion flag and cancels the threshold timer — server
         * is responding again.
         */
        rpcRecovered(): void {
          if (store.firstUnacknowledgedFailureAt() === null) return;
          cancelRpcFailureThresholdTimer();
          patchState(store, {
            firstUnacknowledgedFailureAt: null,
            rpcFailurePromoted: false,
          });
          // The banner-visible / expiry timers may need to drop now that
          // effectiveDisconnectedSince has flipped back to null.
          rescheduleTimers();
        },

        /**
         * A retry burst exhausted. Drains the transient `rejoinInFlight` flag
         * so the next user interaction can retry. Does NOT mutate slot state —
         * exceptions tell us nothing about the server's slot decisions.
         */
        rejoinInvocationFailed(): void {
          patchState(store, { rejoinInFlight: false });
        },

        /** Mark the form dirty/clean. Wired from `<app-annotation-form (isDirty)>`. */
        setFormDirty(value: boolean): void {
          patchState(store, { formDirty: value });
        },

        /** Mark whether the reviewer holds a saved AnnotationSession (the bypass rule). */
        setHasSavedSession(value: boolean): void {
          patchState(store, { hasSavedSession: value });
        },

        /**
         * Invoked by the component on every transition clean→dirty (and on
         * every input event after that). When the user is in a "released, room
         * available" state, ask the server for a fresh slot. Throttled by an
         * in-flight flag so rapid typing produces one invocation per
         * state-entry; the flag clears when the next snapshot arrives or the
         * server denies the rejoin.
         */
        tryReacquireOnInteraction(): void {
          const display = store.presenceDisplayState();
          if (
            display !== 'releasedRoomAvailable' &&
            display !== 'releasedDirtyRoomAvailable'
          )
            return;
          if (store.rejoinInFlight()) return;

          const projectId = store.activeProjectId();
          const stageId = store.activeStageId();
          const studyId = store.activeStudyId();
          if (!projectId || !stageId || !studyId) return;

          patchState(store, { rejoinInFlight: true });
          signalR.joinStudyReview(projectId, stageId, studyId);
        },

        /**
         * Handle a structured access result from either channel (REST or SignalR).
         * When the server denies access, open the AccessDeniedDialog once per study
         * and honour the reviewer's choice: "next-study" routes to the next-random-
         * study feed; "view-details" closes the dialog with the lock overlay in place.
         */
        handleAccessResult(
          result: Pick<
            StudyReviewAccessResult,
            'granted' | 'snapshot' | 'capacityEnforced'
          > & {
            denialReason?: unknown;
            claimRelease?: { studyId: string } | null;
          },
          context: {
            projectId: string;
            stageId: string;
          }
        ): void {
          patchState(store, { capacityEnforced: result.capacityEnforced });
          if (result.granted) return;
          // D8 decision 2: the denial is the release of the reviewer's own leftover claim, which the release
          // snackbar explains; the capacity dialog would give the wrong reason.
          if (result.claimRelease || result.denialReason === 'ClaimReleased')
            return;

          const studyId = result.snapshot.studyId;
          if (deniedStudyIds.has(studyId)) return;
          deniedStudyIds.add(studyId);

          const dialogRef = dialog.open<
            AccessDeniedDialogComponent,
            unknown,
            AccessDeniedDialogResult
          >(AccessDeniedDialogComponent, {
            width: '480px',
            data: { reason: typeof result.denialReason === 'string' ? result.denialReason : null },
            disableClose: false,
            ariaLabel: 'Access denied notification',
          });

          dialogRef.afterClosed().subscribe((action) => {
            if (action === 'next-study') {
              // Route to the stage review index — the default resolver picks the
              // next available study for this reviewer, matching the Next button.
              const projectId = context.projectId || currentProjectId();
              if (projectId) {
                router.navigate([
                  'projects',
                  projectId,
                  'stage',
                  context.stageId,
                  'review',
                ]);
              }
            }
            // On "view-details" (or dismissed via Esc), leave the reviewer on
            // the current page — the surplus-lock overlay already covers the
            // annotation form, so there's nothing further to do here.
          });
        },
      };
    }
  ),

  // ── Side effects: surplus-dialog transition observer ──
  // Threshold timers live in `withMethods` (rescheduled imperatively from
  // each lifecycle method), since signal-store effects don't compose cleanly
  // with `setTimeout` under fakeTimers and the reschedule trigger points are
  // explicit anyway.
  withHooks({
    onInit(store, dialog = inject(MatDialog)) {
      // Fire the surplus dialog on every transition into a surplus state.
      // Replaces the old per-study-id dedup (notifiedStudyIds): if the user
      // exits surplus (a slot opens up) and then re-enters (someone else joins),
      // the dialog fires again. This is a transition event, not a session-flag.
      let previousDisplay: PresenceDisplayState | null = null;
      effect(() => {
        const current = store.presenceDisplayState();
        if (
          previousDisplay !== null &&
          current !== previousDisplay &&
          SURPLUS_DISPLAY_STATES.has(current) &&
          !SURPLUS_DISPLAY_STATES.has(previousDisplay)
        ) {
          dialog.open(SurplusNotificationDialogComponent, {
            width: '480px',
            disableClose: false,
            ariaLabel: 'Session released notification',
          });
        }
        previousDisplay = current;
      });
    },
  })
);
